import React from 'react';
import propTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Actions from '../../../../../store/actions';
import getToken from '../../../../../utility/getToken';
import CommentsController from '../../../../../utility/commentsController';
import { FormatTypes } from '../../../../../constants/templateDictionaries';
import { getSectionKey } from '../../editResume/EditSpace/EditorPane/RenderContent/showResumeSection';
import { CommentBGColor } from '../../../../../styling/theme';

const CommentsContainer = styled.div`
  display: inline-block;
  position: relative;
  background-color: ${(props) => props.commentBG && CommentBGColor};
`;

const Underline = styled.span`
  text-decoration: none;
  border-bottom: 3px solid purple;
  width: 100%;
`;

class Comment extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      commentBG: false,
    };

    const {
      removeOnNextLoad: { comments },
      editor,
      mark,
      updateEventTypes,
      history,
    } = props;

    this.commentRef = React.createRef();

    const commentId = mark.data.get('commentId');
    /**
     * If comment id matches in the remove_on_next_load
     *  Dispatch action to create eventTypes array with type "remove_on_next_load"
     * toggle mark if matches the remove on next load comments
     */
    const {
      tail: { prev, next },
    } = history;
    const commentExist = !prev && !next && comments.includes(commentId);
    if (commentExist) {
      updateEventTypes({ type: 'REMOVE_COMMENT', data: { commentId } });
      editor.moveToStartOfText().moveFocusToEndOfText().removeMark({
        type: 'comment',
        data: {
          commentId,
        },
      });
    }
    this.selectRef = React.createRef();
  }

  componentDidMount = () => {
    document.addEventListener('click', this.handleClickOutside);
  };

  componentDidUpdate = () => {
    const commentPopUpCancelButtonRef = document.querySelector(
      '#commentPopUpCancelButton',
    );
    if (commentPopUpCancelButtonRef) {
      commentPopUpCancelButtonRef.addEventListener('mousedown', () => {
        this.setState({ commentBG: false });
      });
    }
  };

  componentWillUnmount = () => {
    document.removeEventListener('click', this.handleClickOutside);
    const commentPopUpCancelButtonRef = document.querySelector(
      '#commentPopUpCancelButton',
    );
    if (commentPopUpCancelButtonRef) {
      commentPopUpCancelButtonRef.removeEventListener('mousedown', () => {});
    }
  };

  handleClickOutside = (e) => {
    const commentPopUpWrapperRef = document.querySelector(
      '#commentPopUpWrapper',
    );

    if (!this.commentRef || !commentPopUpWrapperRef) {
      this.setState({ commentBG: false });
      return;
    }

    const isInsideCommentRef =
      this.commentRef?.current?.contains(e.target) || null;
    const isInsidePopUpRef = commentPopUpWrapperRef?.contains(e.target) || null;

    if (!isInsideCommentRef && !isInsidePopUpRef)
      this.setState({ commentBG: false });
  };

  getComments = async () => {
    const {
      updatePopUpKeys,
      addComment,
      editor: {
        value: { focusBlock },
      },
    } = this.props;
    if (focusBlock) {
      const { documentId, section, commentsData } = this.getDocData();
      const { type: focusBlockType } = focusBlock;

      const currentCommentIds = this.getActiveMarks();
      if (currentCommentIds.length) {
        const commentCardStatus = currentCommentIds.length === 1;
        const commentCard = {
          focusBlockType,
          status: commentCardStatus,
        };
        updatePopUpKeys({
          commentCard,
          isContextMenu: commentCardStatus
            ? {}
            : { ...commentCard, status: !commentCardStatus },
          loading: commentCard.status,
          currentCommentIds,
        });
        if (commentCard.status && !commentsData[currentCommentIds[0]]) {
          await this.getCommentData({
            commentId: currentCommentIds[0],
            documentId,
            section,
            addComment,
          });
        }
        updatePopUpKeys({
          activeCommentId: commentCard.status ? currentCommentIds[0] : '',
          loading: false,
        });
      }
    }
  };

  getTitle = ({ leftPane }) => {
    if (!leftPane) return 'Cover Letter Main';
    const { activeElement } = leftPane;
    return getSectionKey(activeElement);
  };

  getDocData = () => {
    const {
      userDocument: { documentId, userPreferences },
      comments: { commentsData },
    } = this.props;
    const section = this.getTitle(userPreferences);
    return { documentId, section, commentsData };
  };

  getActiveMarks = () => {
    const { editor } = this.props;
    let activeMarks = JSON.stringify(editor.value.activeMarks);
    activeMarks = JSON.parse(activeMarks);
    if (activeMarks.length)
      return activeMarks
        .filter(({ type }) => type === 'comment')
        .map(({ data: { commentId } }) => commentId);
    return [];
  };

  getCommentData = async ({ commentId, documentId, section, addComment }) => {
    const token = await getToken();
    const { status, data } = await CommentsController.commentActions(
      { commentId, documentId, section },
      token,
      '/getComment',
    );
    if (status) {
      addComment({
        commentId: Object.keys(data)[0],
        data: data[commentId],
      });
    }
  };

  setCommentBG = () => {
    this.setState({ commentBG: true });
  };

  render() {
    const {
      attributes,
      children,
      removeOnNextLoad: { comments },
      mark,
    } = this.props;

    const { commentBG } = this.state;

    const commentId = mark.data.get('commentId');

    return comments.includes(commentId) ? (
      <span>{children}</span>
    ) : (
      <CommentsContainer
        {...attributes}
        onClick={this.getComments} // ! check why onMouseDown is not working
        onMouseDown={this.setCommentBG}
        data-testid="comments_container"
        ref={this.commentRef}
        commentBG={commentBG}
      >
        <Underline
          ref={this.selectRef}
          className="underline_highlight"
          data-testid="underline_highlight"
        >
          {children}
        </Underline>
      </CommentsContainer>
    );
  }
}

Comment.propTypes = {
  updatePopUpKeys: propTypes.func.isRequired,
  comments: propTypes.shape({
    top: propTypes.number.isRequired,
    left: propTypes.number.isRequired,
    isContextMenu: propTypes.object.isRequired,
    currentCommentIds: propTypes.arrayOf(
      propTypes.oneOfType([propTypes.object, propTypes.string]),
    ),
    activeCommentId: propTypes.string.isRequired,
    commentsData: propTypes.object.isRequired,
  }).isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  updatePopUpKeys: (params) => {
    dispatch(Actions.commentActions.updatePopUpKeys(params));
  },
  addComment: (params) => {
    dispatch(Actions.commentActions.addComment(params));
  },
  updateEventTypes: (params) => {
    dispatch(Actions.editResumeFormActions.updateEventTypes(params));
  },
});

const mapStateToProps = (state, ownProps) => {
  const { formatType } = ownProps;
  const common = {
    comments: state.commentsReducer,
    removeOnNextLoad: state.editResumeFormReducer.removeOnNextLoad,
  };
  if (formatType === FormatTypes.RESUME) {
    return {
      userDocument: state.editResumeFormReducer,
      history: state.editResumeFormReducer.history,
      ...common,
    };
  }

  if (formatType === FormatTypes.COVER_LETTER) {
    return {
      userDocument: state.editCoverLetterFormReducer,
      history: state.editCoverLetterFormReducer.history,
      ...common,
    };
  }

  return null;
};

export default connect(mapStateToProps, mapDispatchToProps)(Comment);
